/** Editorial content (`PLATFORMS` and `buildFirstTradeSteps`) for Account Setup. */

import { NA, usd } from '../../utilities/format';
import { t } from '../../i18n/i18n';
import { MT5_MACOS_URL, MT5_WINDOWS_URL } from '../Downloads/data';
import { HELP_CENTRE_URL, TRADING_RULES } from '../HelpCentre/data';

const ruleArticle = (id) => TRADING_RULES.find((rule) => rule.id === id)?.href;

const rulesLink = (id, label = 'Open the rules.') => {
  const href = ruleArticle(id);
  // No article for that rule — the Help Centre index is still better than a
  // dead link, and it lists every rule.
  return href ? { label: t(label), href } : { label: t(label), to: '/help-centre' };
};

/**
 * "Get the platform" — one row per installer.
 *
 * The Windows and macOS rows reuse the MetaTrader 5 installer URLs from the
 * Downloads page, so the two pages cannot drift apart. The combined mobile row
 * has two stores but room for one destination, so it routes to Downloads, where
 * the App Store and Google Play rows sit side by side.
 */
export const PLATFORMS = [
  {
    id: 'windows',
    label: 'Windows',
    meta: 'Installer, 62 MB',
    logo: '/images/platform-windows.svg',
    href: MT5_WINDOWS_URL,
  },
  {
    id: 'macos',
    label: 'macOS',
    meta: 'Installer, 78 MB',
    logo: '/images/platform-macos.svg',
    href: MT5_MACOS_URL,
  },
  {
    id: 'mobile',
    label: 'iOS and Android',
    meta: 'App Store and Google Play',
    // The reference uses the Apple mark for the combined row.
    logo: '/images/platform-ios.svg',
    to: '/downloads',
  },
];

/**
 * "Before your first trade", built from the account's own `before_first_trade`
 * block in the account-setup response rather than written once for every plan:
 *
 *   max_drawdown / max_daily_drawdown  { amount, percent }  always shown
 *   max_risk_per_asset                 { amount, percent }  its own step, when not null
 *   profit_target                      null once funded     step dropped when null
 *   maximum_trading_days               appended to the target when above 0
 *   trade_through_news / hold_over_the_weekend              4th step, when either is on
 *   minimum_trading_days               appended to the target when above 0
 *
 * Numbered rather than bulleted because the order is the point — the two limits
 * that can end the account, then the target, then the rules that catch people
 * out. `link` renders inline at the end of `body`.
 *
 * `profit_target` has only been seen as null, so it is read defensively: either
 * `{ amount, percent }` like the drawdowns or a bare dollar figure.
 */
const pct = (v) => (Number.isFinite(Number(v)) ? `${Number(v)}%` : null);

const limit = ({ amount, percent } = {}) => {
  const figures = [pct(percent), Number.isFinite(Number(amount)) ? usd(amount) : null].filter(Boolean);
  return figures.length > 1 ? `${figures[0]} (${figures[1]})` : figures[0] ?? NA;
};

export function buildFirstTradeSteps(rules, accountSize) {
  if (!rules) return [];

  const steps = [
    {
      id: 'max-loss',
      title: t('Know your max loss'),
      body: t('Max drawdown {{limit}}. It is tracked in Account Metrics.', {
        limit: limit(rules.max_drawdown),
      }),
      link: rulesLink('max-drawdown', 'How max drawdown works.'),
    },
    {
      id: 'max-daily-loss',
      title: t('Know your max daily loss'),
      body: t(
        'Max daily drawdown {{limit}}, measured at the daily candle open, 00:00 broker time.',
        { limit: limit(rules.max_daily_drawdown) },
      ),
      link: rulesLink('daily-drawdown', 'How the daily limit works.'),
    },
  ];

  // The Max Risk rule — null on the plans it doesn't apply to.
  const risk = rules.max_risk_per_asset;
  if (risk != null && risk !== '') {
    steps.push({
      id: 'max-risk',
      title: t('Know your max risk per asset'),
      body: t('Max {{limit}} open loss on any one asset.', {
        limit: typeof risk === 'object' ? limit(risk) : pct(risk) ?? NA,
      }),
      link: rulesLink('max-risk', 'How the max risk rule works.'),
    });
  }

  // Funded accounts have no target, and the API says so with null.
  const target = rules.profit_target;
  if (target != null && target !== '') {
    const amount = typeof target === 'object' ? target.amount : target;
    const percent =
      typeof target === 'object'
        ? target.percent
        : Number(accountSize) > 0 && Number.isFinite(Number(amount))
          ? (Number(amount) / Number(accountSize)) * 100
          : null;
    const minDays = Number(rules.minimum_trading_days);
    const maxDays = Number(rules.maximum_trading_days);
    steps.push({
      id: 'target',
      title: t('Know your Profit Target'),
      body:
        t('{{limit}} on this plan.', { limit: limit({ amount, percent }) }) +
        (minDays > 0 ? ` ${t('Trade at least {{days}} days.', { days: minDays })}` : '') +
        (maxDays > 0 ? ` ${t('Complete it within {{days}} days.', { days: maxDays })}` : ''),
    });
  }

  const allowed = [
    rules.trade_through_news && t('You may trade through high-impact news releases.'),
    rules.hold_over_the_weekend && t('Positions may be held over the weekend.'),
  ].filter(Boolean);
  if (allowed.length) {
    steps.push({
      id: 'trading-rules',
      title: t('Know what this plan allows'),
      body: allowed.join(' '),
      // News and weekend holding — the article that covers both.

      link: { label: t('Open the rules.'), href: HELP_CENTRE_URL },
    });
  }

  return steps;
}
